import {
	DialogCompat as Dialog,
	DialogType,
	IDialogContentProps,
	IDialogProps,
	IPersona,
} from 'components/FluentUICompat';
import {useTranslation} from 'react-i18next';
import React from 'react';
import {LabelWithTooltip} from 'features/localizedTooltips';
import {PeoplePicker} from 'components/hookForms/ControlledPeoplePicker';
import {RequirementChangeType, User, UserRole} from 'types';
import {
	SendApprovalDialogChangeTypeMode,
	WORKFLOW_DIALOG_MODAL_PROPS,
	WorkflowChangeTypeDropdown,
	WorkflowDialogFooter,
	WorkflowReasonField,
} from './WorkflowDialogShared';

export {SendApprovalDialogChangeTypeMode} from './WorkflowDialogShared';

export type SendApprovalDialogProps = Omit<IDialogProps, 'modalProps'> & {
	onConfirm: () => void;
	requirementName: string;
	defaultApprovers: User[];
	users: User[]; // Add users state as a prop
	setUsers: (users: User[]) => void; // Add setUsers as a prop
	changeType: RequirementChangeType;
	setChangeType: (changeType: RequirementChangeType) => void;
	changeTypeMode: SendApprovalDialogChangeTypeMode;
	reason: string;
	setReason: (res: string) => void;
};

export const SendApprovalDialog: React.FC<SendApprovalDialogProps> = ({
	defaultApprovers,
	requirementName,
	users,
	setUsers,
	changeType,
	setChangeType,
	changeTypeMode,
	reason,
	setReason,

	...props
}) => {
	const {t} = useTranslation('features/requirements', {
		keyPrefix: 'WorkflowDialog',
	});
	const onUserInputChange = React.useCallback(
		(items: any) => setUsers(items || []),
		[users],
	);

	const [submitDisabled, setSubmitDisabled] = React.useState(true);
	const [peoplePickerDisabled, setPeoplePickerDisabled] = React.useState(false);

	const [peoplePickerRoleFilter, setPeoplePickerRoleFilter] = React.useState([
		UserRole.ShApprover,
	]);
	const [defaultSelectedApprovers, setDefaultSelectedApprovers] =
		React.useState<IPersona[]>([]);
	const [peoplePickerKey, setPeoplePickerKey] = React.useState(0);

	const dismiss = () => {
		props?.onDismiss?.();
	};

	const dialogContentProps: IDialogContentProps = React.useMemo(
		() => ({
			type: DialogType.normal,
			title: t('Approval'),
			subText: t('ConfirmApprovalText', {name: requirementName}),
		}),
		[],
	);
	const handleSubmitDisabled = () => {
		const usersRequired = (users?.length || 0) === 0;
		const changeTypeRequired =
			changeTypeMode === SendApprovalDialogChangeTypeMode.Show &&
			changeType === RequirementChangeType.None;
		setSubmitDisabled(usersRequired || changeTypeRequired);
	};

	React.useEffect(() => {
		handleSubmitDisabled();
	}, [users]);

	React.useEffect(() => {
		if (changeType === RequirementChangeType.Editorial) {
			setDefaultSelectedApprovers([]);
			setUsers([]);
			setPeoplePickerRoleFilter([UserRole.ShApprover, UserRole.Vex]);
			setPeoplePickerDisabled(false);
		} else {
			setDefaultSelectedApprovers(
				defaultApprovers?.map((v: User) => ({
					...v,
					text: v?.name,
				})),
			);
			setUsers(defaultApprovers);
			setPeoplePickerRoleFilter([UserRole.ShApprover]);
			setPeoplePickerDisabled(defaultApprovers?.length >= 1);
		}

		handleSubmitDisabled();
		setPeoplePickerKey(Math.random());
	}, [changeType]);

	return (
		<Dialog
			dialogContentProps={dialogContentProps}
			{...props}
			onDismiss={dismiss}
			modalProps={WORKFLOW_DIALOG_MODAL_PROPS}
		>
			<>
				<WorkflowChangeTypeDropdown
					changeType={changeType}
					changeTypeMode={changeTypeMode}
					setChangeType={setChangeType}
				/>
				<LabelWithTooltip translationKey='users'>
					{t('ChoosePersonLabel')}
				</LabelWithTooltip>
				<PeoplePicker
					key={peoplePickerKey}
					onChange={items => onUserInputChange(items ?? [])}
					itemLimit={1}
					excludeCurrentUser={true}
					defaultSelectedItems={defaultSelectedApprovers}
					disabled={peoplePickerDisabled}
					roles={peoplePickerRoleFilter}
				/>
				<WorkflowReasonField setReason={setReason} />
				<WorkflowDialogFooter
					onConfirm={props.onConfirm}
					onDismiss={dismiss}
					submitDisabled={submitDisabled}
				/>
			</>
		</Dialog>
	);
};
